'use client';

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { FiChevronLeft, FiChevronRight, FiLinkedin, FiUser, FiPause, FiPlay } from 'react-icons/fi';
import Image from 'next/image';

interface Testimonial {
  id: number;
  author: string;
  position: string | null;
  company: string | null;
  avatar: string | null;
  content: string;
  socialLink: string | null;
}

export default function TestimonialCarousel({ testimonials }: { testimonials: Testimonial[] }) {
  const [index, setIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const next = useCallback(() => setIndex((prev) => (prev + 1) % testimonials.length), [testimonials.length]);
  const prev = useCallback(() => setIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length), [testimonials.length]);

  // Autoplay logic
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(next, 6000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, next]);

  if (!testimonials.length) return null;

  const current = testimonials[index];
  const displayPositionCompany = [current.position, current.company].filter(Boolean).join(' // ');

  // Swipe gesture with momentum
  const handleDragEnd = (_: any, info: PanInfo) => {
    const velocity = info.velocity.x;
    const offset = info.offset.x;

    // swipe threshold: offset > 50px OR velocity > 500px/s
    if (offset > 50 || velocity > 500) prev();
    else if (offset < -50 || velocity < -500) next();
  };

  return (
    <div 
      className="relative max-w-5xl mx-auto px-4 py-16"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      <div className="flex flex-col items-center text-center">
        
        {/* Autoplay Toggle */}
        <button
          onClick={() => setIsAutoPlaying(prev => !prev)}
          className="mb-6 flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-white/30"
        >
          {isAutoPlaying ? <FiPlay className="animate-pulse" /> : <FiPause />}
          <span>{isAutoPlaying ? "Auto-Scanning" : "Manual Hold"}</span>
        </button>

        <div className="relative min-h-[350px] md:min-h-[300px] w-full flex items-center justify-center">
          <AnimatePresence mode="wait" custom={index}>
            <motion.div
              key={current.id}
              initial={{ opacity: 0, x: 20, scale: 0.98 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -20, scale: 1.02 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="space-y-8"
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragEnd={handleDragEnd}
            >
              <p className="text-2xl md:text-4xl font-medium italic leading-snug tracking-tight text-white/90">
                &ldquo;{current.content}&rdquo;
              </p>
              
              <div className="flex flex-col items-center gap-4">
                <div className="relative w-16 h-16 rounded-full overflow-hidden border-2 border-indigo-500/30 ring-4 ring-white/5">
                  {current.avatar ? (
                    <Image 
                      src={current.avatar} 
                      alt={current.author} 
                      fill 
                      className="object-cover" 
                      unoptimized 
                    />
                  ) : (
                    <div className="w-full h-full bg-zinc-800 flex items-center justify-center text-zinc-400">
                      <FiUser size={24} />
                    </div>
                  )}
                </div>
                
                <div>
                  <div className="flex items-center justify-center gap-2">
                    <h4 className="text-lg font-black uppercase tracking-tighter text-white">
                      {current.author}
                    </h4>
                    {current.socialLink && (
                      <a href={current.socialLink} target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-indigo-400 transition-colors">
                        <FiLinkedin size={14} />
                      </a>
                    )}
                  </div>
                  {displayPositionCompany && (
                    <p className="text-[10px] font-black text-indigo-300 uppercase tracking-[0.3em]">
                      {displayPositionCompany}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation & Progress */}
        <div className="flex items-center gap-6 mt-12">
          <button 
            onClick={prev} 
            aria-label="Previous testimonial"
            className="p-3 rounded-xl bg-white/5 hover:bg-white/10 text-white transition-all border border-white/10 group"
          >
            <FiChevronLeft className="group-active:-translate-x-1 transition-transform" />
          </button>
          
          <div className="flex gap-3">
            {testimonials.map((_, i) => (
              <button 
                key={i} 
                onClick={() => setIndex(i)}
                className="relative h-1.5 rounded-full bg-white/10 overflow-hidden transition-all duration-500"
                style={{ width: i === index ? '40px' : '8px' }}
              >
                {i === index && (
                  <motion.div 
                    key={index} // resets animation on change
                    initial={{ width: 0 }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 6, ease: "linear" }}
                    className={`absolute inset-0 ${isAutoPlaying ? 'bg-indigo-500' : 'bg-white'}`}
                  />
                )}
              </button>
            ))}
          </div>

          <button 
            onClick={next} 
            aria-label="Next testimonial"
            className="p-3 rounded-xl bg-white/5 hover:bg-white/10 text-white transition-all border border-white/10 group"
          >
            <FiChevronRight className="group-active:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );
}
